import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { RangeValue } from "@/shared/ui";
import { usePendingActions } from "./usePendingActions";
import { getItemCategory } from "../consts";
import type { PendingCategory } from "../consts";
import type { Priority } from "../types/pending.types";

export type SortOption = "priority_desc" | "priority_asc" | "date_desc" | "date_asc";

const PRIORITY_WEIGHT: Record<Priority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

export function usePendingActionsPage(options?: { pauseRefetch?: boolean }) {
  const { t } = useTranslation("PendingActionsPage");
  const { items, summary, isLoading, refetch } = usePendingActions(options);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Set<PendingCategory>>(new Set());
  const [selectedPriorities, setSelectedPriorities] = useState<Set<Priority>>(new Set());
  const [dateRange, setDateRange] = useState<RangeValue | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("priority_desc");

  const handleCategoryToggle = useCallback(function handleCategoryToggle(category: PendingCategory) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const handlePriorityToggle = useCallback(function handlePriorityToggle(priority: Priority) {
    setSelectedPriorities((prev) => {
      const next = new Set(prev);
      if (next.has(priority)) {
        next.delete(priority);
      } else {
        next.add(priority);
      }
      return next;
    });
  }, []);

  const handleResetFilters = useCallback(function handleResetFilters() {
    setSearchQuery("");
    setSelectedCategories(new Set());
    setSelectedPriorities(new Set());
    setDateRange(null);
  }, []);

  const hasActiveFilters =
    searchQuery.length > 0 ||
    selectedCategories.size > 0 ||
    selectedPriorities.size > 0 ||
    dateRange !== null;

  const filteredItems = useMemo(() => {
    let result = items;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) =>
        item.title.toLowerCase().includes(q),
      );
    }

    if (selectedCategories.size > 0) {
      result = result.filter((item) =>
        selectedCategories.has(getItemCategory(item.originalType)),
      );
    }

    if (selectedPriorities.size > 0) {
      result = result.filter((item) =>
        selectedPriorities.has(item.priority),
      );
    }

    if (dateRange) {
      result = result.filter((item) => {
        const createdDate = new Date(item.createdAt);
        createdDate.setHours(0, 0, 0, 0);
        if (dateRange.start) {
          const start = new Date(dateRange.start);
          start.setHours(0, 0, 0, 0);
          if (createdDate < start) return false;
        }
        if (dateRange.end) {
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999);
          if (createdDate > end) return false;
        }
        return true;
      });
    }

    result = [...result].sort((a, b) => {
      if (sortBy === "priority_desc") return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
      if (sortBy === "priority_asc") return PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
      if (sortBy === "date_desc") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    return result;
  }, [items, searchQuery, selectedCategories, selectedPriorities, dateRange, sortBy]);

  return {
    t,
    items,
    filteredItems,
    summary,
    isLoading,
    refetch,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    selectedPriorities,
    handlePriorityToggle,
    dateRange,
    setDateRange,
    sortBy,
    setSortBy,
    handleResetFilters,
    hasActiveFilters,
  };
}
